import localForage from 'localforage';

const MEDIA_CLIPBOARD_KEY = 'latest-media-entry';
const MEDIA_CLIPBOARD_TTL = 5 * 60 * 1000;
const DEFAULT_FILE_PREFIX = 'clipboard-media';
const INVALID_FILE_NAME_PATTERN = /[\\/:*?"<>|]+/g;
const MIME_EXTENSION_MAP = {
	'image/png': 'png',
	'image/jpeg': 'jpg',
	'image/jpg': 'jpg',
	'image/webp': 'webp',
	'image/gif': 'gif',
	'video/mp4': 'mp4',
	'video/ogg': 'ogv',
	'video/webm': 'webm',
	'video/quicktime': 'mov'
};

const mediaClipboardStore = localForage.createInstance({
	name: 'boxim-media-clipboard',
	storeName: 'media_clipboard'
});

let memoryEntry = null;

function sanitizeFileName(fileName) {
	const normalized = String(fileName || '').trim().replace(INVALID_FILE_NAME_PATTERN, '_');
	return normalized || '';
}

function normalizeKind(kind) {
	return kind === 'video' ? 'video' : 'image';
}

function normalizeMimeType(blob, mimeType, kind) {
	if (blob && typeof blob.type === 'string' && blob.type) {
		return blob.type;
	}
	if (mimeType) {
		return String(mimeType);
	}
	return kind === 'video' ? 'video/mp4' : 'image/png';
}

function resolveExtension(mimeType, fileName) {
	const sanitizedName = sanitizeFileName(fileName);
	if (sanitizedName.includes('.')) {
		return sanitizedName.split('.').pop();
	}
	return MIME_EXTENSION_MAP[mimeType] || (mimeType && mimeType.includes('/') ? mimeType.split('/').pop() : 'bin');
}

function buildFallbackFileName(kind, mimeType, fileName, copiedAt) {
	const sanitizedName = sanitizeFileName(fileName);
	if (sanitizedName) {
		return sanitizedName;
	}
	const extension = resolveExtension(mimeType, sanitizedName);
	return `${DEFAULT_FILE_PREFIX}-${normalizeKind(kind)}-${copiedAt}.${extension}`;
}

function isBlobLike(value) {
	return typeof Blob !== 'undefined' && value instanceof Blob;
}

export function isVideoFile(file) {
	return Boolean(file && typeof file.type === 'string' && file.type.indexOf('video/') === 0);
}

export async function saveMediaClipboardEntry(payload = {}) {
	const blob = payload.blob;
	if (!isBlobLike(blob)) {
		throw new Error('媒体内容为空');
	}
	const copiedAt = Date.now();
	const kind = normalizeKind(payload.kind);
	const mimeType = normalizeMimeType(blob, payload.mimeType, kind);
	const entry = {
		kind,
		blob,
		mimeType,
		sourceUrl: String(payload.sourceUrl || ''),
		textFallback: String(payload.textFallback || payload.sourceUrl || ''),
		fileName: buildFallbackFileName(kind, mimeType, payload.fileName, copiedAt),
		copiedAt
	};
	await mediaClipboardStore.setItem(MEDIA_CLIPBOARD_KEY, entry);
	memoryEntry = entry;
	return entry;
}

export async function clearMediaClipboardEntry() {
	memoryEntry = null;
	await mediaClipboardStore.removeItem(MEDIA_CLIPBOARD_KEY);
}

export async function readMediaClipboardEntry(options = {}) {
	const {
		maxAgeMs = MEDIA_CLIPBOARD_TTL,
		clipboardText = ''
	} = options;
	const entry = memoryEntry || await mediaClipboardStore.getItem(MEDIA_CLIPBOARD_KEY);
	if (!entry || !isBlobLike(entry.blob)) {
		return null;
	}
	if (Date.now() - Number(entry.copiedAt || 0) > maxAgeMs) {
		await clearMediaClipboardEntry();
		return null;
	}
	const normalizedClipboardText = String(clipboardText || '').trim();
	const textFallback = String(entry.textFallback || '').trim();
	if (normalizedClipboardText && textFallback && normalizedClipboardText !== textFallback) {
		return null;
	}
	memoryEntry = entry;
	return entry;
}

export function createMediaClipboardFile(entry) {
	if (!entry || !isBlobLike(entry.blob)) {
		return null;
	}
	const mimeType = normalizeMimeType(entry.blob, entry.mimeType, entry.kind);
	const fileName = buildFallbackFileName(entry.kind, mimeType, entry.fileName, entry.copiedAt || Date.now());
	return new File([entry.blob], fileName, {
		type: mimeType
	});
}
